import { BrowserRouter, HashRouter } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { AppRoutes } from "./AppRoutes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnalyticsWrapper } from "@/components/common/AnalyticsWrapper";
import { useEffect } from "react";
import { initializeSecureAuthHandling } from "@/utils/authService";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ChatSidebarProvider } from "@/contexts/ChatSidebarContext";
import { supabase } from "@/integrations/supabase/client";
import "./App.css";

// Platform detection
const isDesktop = typeof window !== 'undefined' && '__TAURI__' in window;

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60, // 1 hour
      retry: 3,
    },
  },
});

function App() {
  // Initialize secure HttpOnly cookie auth handling
  useEffect(() => {
    initializeSecureAuthHandling();
  }, []);

  // Handle password recovery detection
  useEffect(() => {
    // Check for password recovery in URL (both hash and search params)
    const checkPasswordRecovery = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      
      // Debug logging to see what's actually in the URL
      console.log('[Auth] Checking recovery - URL:', window.location.href);
      console.log('[Auth] Search params:', window.location.search);
      console.log('[Auth] Hash params:', window.location.hash);
      console.log('[Auth] URL type param:', urlParams.get('type'));
      console.log('[Auth] Hash type param:', hashParams.get('type'));
      console.log('[Auth] Document referrer:', document.referrer);
      
      // Check if coming from Supabase auth server (server-side verification)
      if (document.referrer && document.referrer.includes('auth.strykr.ai')) {
        console.log('[Auth] Detected redirect from Supabase auth server, checking for recovery session');
        
        // Check if we have a session (which would indicate successful verification)
        supabase.auth.getSession().then(({ data: { session }, error }) => {
          if (session) {
            console.log('[Auth] Recovery session found, redirecting to reset password');
            window.location.href = '/reset-password';
          } else {
            console.log('[Auth] No session found from auth server redirect');
          }
        });
        return;
      }
      
      // Check URL search params (client-side verification flow)
      if (urlParams.get('type') === 'recovery') {
        console.log('[Auth] Recovery detected in URL params, redirecting to reset password');
        // Clear URL params and redirect to reset password page
        window.history.replaceState(null, '', window.location.pathname);
        window.location.href = '/reset-password';
        return;
      }
      
      // Check hash params (client-side verification flow)
      if (hashParams.get('type') === 'recovery') {
        console.log('[Auth] Recovery detected in URL hash');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (accessToken) {
          // Set the session with the tokens from the URL
          supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          }).then(() => {
            // Clear the hash from URL and redirect to reset password page
            window.history.replaceState(null, '', window.location.pathname);
            window.location.href = '/reset-password';
          }).catch((error) => {
            console.error('[Auth] Error setting session from recovery link:', error);
            // Redirect to forgot password page on error
            window.location.href = '/auth?view=forgot_password';
          });
        }
      }
    };

    // Check on initial load
    checkPasswordRecovery();

    // Also listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] Auth state change:', event, session ? 'with session' : 'no session');
      
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[Auth] PASSWORD_RECOVERY event detected, redirecting to reset password');
        // Direct redirect to reset password form
        window.location.href = '/reset-password';
      }
      
      // Also check if we have a session and are coming from a recovery flow
      if (event === 'SIGNED_IN' && session) {
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        if (urlParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery') {
          console.log('[Auth] User signed in after recovery, redirecting to reset password');
          // Clear URL and redirect to reset password page
          window.history.replaceState(null, '', window.location.pathname);
          window.location.href = '/reset-password';
        }
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Use HashRouter for desktop (Tauri) and BrowserRouter for web
  const Router = isDesktop ? HashRouter : BrowserRouter;

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AnalyticsWrapper>
          <SubscriptionProvider>
            <ChatSidebarProvider>
              <AppRoutes />
              <Toaster />
            </ChatSidebarProvider>
          </SubscriptionProvider>
        </AnalyticsWrapper>
      </Router>
    </QueryClientProvider>
  );
}

export default App;